import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
  Subject,
  debounceTime,
  of,
  catchError,
  merge,
  timer,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationService } from 'src/app/services/location.service';
import {
  EventWeatherForecast,
  WeatherSlotsResponse,
} from 'src/app/models/location.models';
import {
  ACTIVITY_TYPE_LABELS,
  SERVICE_LABELS,
  AFFLUENCE_LABELS,
} from 'src/app/models/onboarding.labels';
import { ServiceTime, Affluence } from 'src/app/models/onboarding.model';
import { AuthService } from 'src/app/services/auth.service';
import { EventService } from 'src/app/services/event.service';
import {
  AppEvent,
  EVENT_TYPES,
  WEATHER_ICONS,
} from 'src/app/models/event.model';
import {
  DashboardItinerantService,
  ItinerantKPIs,
  WeatherAlert,
  AiInsight,
  TimelineDay,
  TopEvent,
  Opportunity,
  EventsByStatus,
  RecoTransparency,
  RecoProduct,
  PrecisionPoint,
  EventChecklist,
  ChecklistItem,
  MerchantMapLocation,
} from 'src/app/services/dashboard-itinerant.service';
import {
  MapWidgetComponent,
  MapEvent,
} from 'src/app/shared/components/map-widget/map-widget.component';
import {
  EventConflictService,
  ConflictInfo,
} from 'src/app/services/event-conflict.service';
import { EventConflictWarningComponent } from 'src/app/shared/components/event-conflict-warning/event-conflict-warning.component';
import { EventRefreshService } from 'src/app/services/event-refresh.service';
import { PlanFeatureService } from 'src/app/services/plan-feature.service';
import { FEATURES } from 'src/app/shared/config/plan-features';
import {
  UnifiedAlertService,
  UnifiedAlert,
} from 'src/app/services/unified-alert.service';
import { LogService } from '../../../services/log.service';
import { MenuCardComponent } from 'src/app/shared/components/menu-card/menu-card.component';
import {
  MerchantPeriodService,
  MerchantPeriod,
  PERIOD_TYPE_META,
} from 'src/app/services/merchant-period.service';
import { SpecialPeriodFormComponent } from 'src/app/shared/components/special-period-form/special-period-form.component';
import { AlertPreviewComponent } from 'src/app/shared/components/alert-preview/alert-preview.component';
import {
  CalendarView,
  CALENDAR_VIEW_META,
  CalendarDay,
} from '../dashboard-fixed/dashboard-fixed.component';

@Component({
  selector: 'app-dashboard-itinerant',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MapWidgetComponent,
    EventConflictWarningComponent,
    MenuCardComponent,
    SpecialPeriodFormComponent,
    AlertPreviewComponent,
  ],
  templateUrl: './dashboard-itinerant.component.html',
  styleUrls: ['./dashboard-itinerant.component.css'],
})
export class DashboardItinerantComponent implements OnInit {
  private log = inject(LogService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  readonly planFeatureService = inject(PlanFeatureService);
  readonly alertService = inject(UnifiedAlertService);
  readonly FEATURES = FEATURES;

  // ─── Profil ─────────────────────────────────────
  businessName = '';
  showMenuCard = false;
  activityType: string = '';
  userServices: ServiceTime[] = [];
  affluence: Affluence | '' = '';
  userAddress: string = ''; // Adresse du commerce

  // Labels pour le template
  activityTypeLabels = ACTIVITY_TYPE_LABELS;
  serviceLabels = SERVICE_LABELS;
  affluenceLabels = AFFLUENCE_LABELS;
  weatherIcons = WEATHER_ICONS;

  // ─── Données dashboard ──────────────────────────
  kpis: ItinerantKPIs | null = null;
  eventsByStatus: EventsByStatus = {
    planned: [],
    active: [],
    completed: [],
    analyzed: [],
  };
  timeline: TimelineDay[] = [];

  // ─── Calendrier ─────────────────────────────────
  private merchantPeriodService = inject(MerchantPeriodService);
  readonly CALENDAR_VIEW_META = CALENDAR_VIEW_META;
  readonly calendarViews: CalendarView[] = ['1w', '1m'];
  calendarView = signal<CalendarView>('1w');
  calendarOffset = signal<number>(0);
  calendarDays: CalendarDay[] = [];
  selectedCalendarDay: CalendarDay | null = null;
  merchantPeriods = signal<MerchantPeriod[]>([]);
  readonly PERIOD_TYPE_META = PERIOD_TYPE_META;
  showPeriodForm = signal(false);
  editingPeriod = signal<MerchantPeriod | null>(null);
  /** Jours dont on a ouvert l'expansion des périodes multiples */
  expandedPeriodDays = new Set<string>();

  togglePeriodExpand(day: CalendarDay, event: Event): void {
    event.stopPropagation();
    const key = new Date(day.date).toDateString();
    if (this.expandedPeriodDays.has(key)) {
      this.expandedPeriodDays.delete(key);
    } else {
      this.expandedPeriodDays.add(key);
    }
    this.cdr.markForCheck();
  }

  isDayPeriodExpanded(day: CalendarDay): boolean {
    return this.expandedPeriodDays.has(new Date(day.date).toDateString());
  }
  weatherAlerts: WeatherAlert[] = [];
  unifiedAlerts: UnifiedAlert[] = [];
  insights: AiInsight[] = [];
  topEvents: TopEvent[] = [];
  opportunities: Opportunity[] = [];

  // ─── Phase 2: Moteur reco ───────────────────────
  precisionHistory: PrecisionPoint[] = [];
  nextEventRecos: RecoTransparency | null = null;
  showRecoDetail = false;

  // ─── Phase 3: Checklist ─────────────────────────
  nextEventChecklist: EventChecklist | null = null;
  checklistFilter: string = 'all';

  // ─── Météo prochain événement ────────────────────
  nextEventWeather: EventWeatherForecast | null = null;

  // ─── Météo timeline (par eventId) ────────────────
  timelineEventWeather = new Map<string, EventWeatherForecast>();

  // ─── Météo horaire événements actifs ───────────────
  activeEventHourlyWeather = new Map<string, WeatherSlotsResponse>();

  // ─── Phase 6: Carte ─────────────────────────────
  merchantLocation: MerchantMapLocation | null = null;
  mapEvents: MapEvent[] = [];
  followedOpportunityIds: string[] = [];
  followedEventMap: Record<string, string> = {}; // externalId -> eventId mapping
  highlightedEventId: string | null = null;
  deleteConfirmEvent: any | null = null; // événement en attente de confirmation de suppression

  // ─── Filtres opportunités ───────────────────────
  filteredOpportunities: Opportunity[] = [];
  oppFilterPeriod: string = 'all';
  oppFilterProfitability: string = 'all';
  oppFilterType: string = 'all';
  oppSortBy: 'date' | 'margin' | 'score' = 'date';
  oppSortDir: 'asc' | 'desc' = 'asc';
  oppShowAll: boolean = false; // Afficher toutes les opportunités ou juste 10

  // ─── UI state ───────────────────────────────────
  loading = true;
  selectedTimelineDay: TimelineDay | null = null;
  isEmptyState = false;

  // ─── Conflict warning ───────────────────────────
  conflictWarning: ConflictInfo[] = [];
  private pendingOpportunity: any = null;

  private radiusChange$ = new Subject<number>();

  // Rayon d'action par défaut selon le type d'activité (en km)
  private readonly DEFAULT_RADIUS_BY_ACTIVITY: Record<string, number> = {
    // Food trucks : se déplacent vers zones d'affluence
    food_truck_burger: 35,
    food_truck_world: 35,
    food_truck_resto: 40,
    // Snacking/Pizza : emplacements réguliers
    sandwich_shop: 25,
    // Marchés, primeurs : circuit court à moyen
    fresh_produce: 30,
    market_deli: 50,
    // Glaces : saisonnalité, événements
    ice_cream: 40,
    // Boulangerie itinérante : tournées régulières
    bakery: 25,
    // Bar/Café mobile : événements
    bar_cafe: 45,
  };
  private readonly DEFAULT_RADIUS = 30; // Défaut général

  constructor(
    private dashboardService: DashboardItinerantService,
    private router: Router,
    private authService: AuthService,
    private locationService: LocationService,
    private eventService: EventService,
    private eventConflictService: EventConflictService,
    private refreshService: EventRefreshService,
  ) {
    // Debounce pour éviter trop de requêtes lors du déplacement du slider
    this.radiusChange$
      .pipe(debounceTime(600), takeUntilDestroyed(this.destroyRef))
      .subscribe((radius) => {
        this.saveRadius(radius);
        // Pas de reloadOpportunities() : le fetch se fait avec un rayon fixe de 100km
        // côté backend, le slider est un filtre d'affichage uniquement.
      });
  }

  formatLocation(loc: any): string {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return loc.city || loc.address || '';
  }

  private rebuildMapEvents(): void {
    this.mapEvents = [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
      ...this.eventsByStatus.completed,
      ...this.eventsByStatus.analyzed,
    ]
      .filter((e: any) => e.lat && e.lng)
      .map((e: any) => ({
        id: e.id,
        name: e.name,
        lat: e.lat,
        lng: e.lng,
        location: e.location,
        eventType: e.eventType,
        status: e.status,
        date: e.eventDate,
      }));
  }

  deleteEvent(event: any, $event: Event): void {
    $event.stopPropagation();
    this.deleteConfirmEvent = event;
  }

  cancelDelete(): void {
    this.deleteConfirmEvent = null;
  }

  confirmDelete(): void {
    const event = this.deleteConfirmEvent;
    if (!event) return;
    this.deleteConfirmEvent = null;

    this.eventService
      .deleteEvent(event.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const eventId = String(event.id);
          // Retirer de tous les buckets
          this.eventsByStatus = {
            planned: this.eventsByStatus.planned.filter(
              (e: any) => String(e.id) !== eventId,
            ),
            active: this.eventsByStatus.active.filter(
              (e: any) => String(e.id) !== eventId,
            ),
            completed: this.eventsByStatus.completed.filter(
              (e: any) => String(e.id) !== eventId,
            ),
            analyzed: this.eventsByStatus.analyzed.filter(
              (e: any) => String(e.id) !== eventId,
            ),
          };
          // Retirer de la timeline
          this.timeline = this.timeline.map((day) => ({
            ...day,
            events: day.events.filter((e: any) => String(e.id) !== eventId),
          }));
          // Resynchroniser selectedTimelineDay avec le nouvel objet (évite l'affichage du deleted)
          if (this.selectedTimelineDay) {
            const prevDate = new Date(
              this.selectedTimelineDay.date,
            ).toDateString();
            this.selectedTimelineDay =
              this.timeline.find(
                (d) => new Date(d.date).toDateString() === prevDate,
              ) ?? null;
          }
          this.rebuildMapEvents();
          if (event.externalId) {
            this.followedOpportunityIds = this.followedOpportunityIds.filter(
              (id) => id !== event.externalId,
            );
            const map = { ...this.followedEventMap };
            delete map[event.externalId];
            this.followedEventMap = map;
          }
          this.applyOpportunityFilters();
          this.isEmptyState =
            this.eventsByStatus.planned.length === 0 &&
            this.eventsByStatus.active.length === 0 &&
            this.eventsByStatus.completed.length === 0 &&
            this.eventsByStatus.analyzed.length === 0;
          this.refreshNextEventWeatherIfChanged();
          this.cdr.markForCheck();
        },
        error: (err) => this.log.error('❌ Erreur suppression événement:', err),
      });
  }

  ngOnInit() {
    // Pré-remplir le header depuis le cache local (instantané, pas d'HTTP)
    const cached = this.authService.getCurrentUser();
    if (cached) {
      this.businessName = (cached as any).businessName || '';
      this.activityType = ((cached as any).activityType as string) || '';
    }

    this.loadBusinessName();
    this.loadMerchantPeriods();

    // ══ Stream réactif principal ══
    // merge(of(void 0)) = émission immédiate (chargement initial)
    // + dataChange$ = toute mutation (create/update/delete) auto-annoncée par EventService
    // switchMap annule la requête en cours si une nouvelle mutation arrive avant la fin
    merge(of<void>(void 0), this.refreshService.dataChange$)
      .pipe(
        tap(() => {
          this.loading = true;
        }),
        switchMap(() =>
          this.dashboardService.loadDashboard().pipe(
            catchError((err) => {
              this.log.error('Erreur chargement dashboard:', err);
              this.loading = false;
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        if (data) {
          this.applyDashboardData(data);
        } else {
          this.loading = false;
        }
        this.cdr.markForCheck();
      });

    // Transitions horodatées (planned→active, active→completed) — local, sans HTTP
    this.refreshService.transitions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reclassifyEventsLocally();
        this.cdr.markForCheck();
      });

    // Reload full à minuit (nouvelle fenêtre 7j)
    const now = new Date();
    const msToMidnight =
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime() -
      now.getTime();
    timer(msToMidnight, 86_400_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshService.trigger());
  }

  // ─── Calendrier ─────────────────────────────────

  private loadMerchantPeriods(): void {
    const today = new Date();
    const end = new Date(today);
    end.setFullYear(today.getFullYear() + 1);
    this.merchantPeriodService.load(today, end).subscribe({
      next: (res) => {
        this.merchantPeriods.set(res.periods);
        this.generateCalendar();
        this.cdr.markForCheck();
      },
      error: () => {
        this.generateCalendar();
      },
    });
  }

  generateCalendar(): void {
    const view = this.calendarView();
    const offset = this.calendarOffset();
    const daysCount = CALENDAR_VIEW_META[view].days;
    const now = new Date();
    let start: Date;
    if (view === '1w') {
      start = new Date(now);
      start.setDate(now.getDate() + offset * 7);
    } else if (view === '1m') {
      start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    } else if (view === '3m') {
      start = new Date(now);
      start.setMonth(now.getMonth() + offset * 3);
    } else {
      start = new Date(now.getFullYear() + offset, 0, 1);
    }
    const days: CalendarDay[] = [];

    for (let i = 0; i < daysCount; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);

      // Marquer les événements planifiés ce jour — chercher dans tous les statuts
      const allEvents = [
        ...this.eventsByStatus.planned,
        ...this.eventsByStatus.active,
        ...this.timeline.flatMap((t) => t.events ?? []),
      ];
      const eventNames: string[] = [];
      const seen = new Set<string>();
      for (const e of allEvents) {
        const ed = new Date((e as any).eventDate ?? (e as any).date ?? '');
        if (
          !isNaN(ed.getTime()) &&
          ed.getFullYear() === date.getFullYear() &&
          ed.getMonth() === date.getMonth() &&
          ed.getDate() === date.getDate()
        ) {
          const name = (e as any).name ?? (e as any).title ?? '';
          if (name && !seen.has(name)) {
            seen.add(name);
            eventNames.push(name);
          }
        }
      }

      const activePeriods =
        this.merchantPeriodService.getAllActiveForDate(date);

      // Météo depuis la timeline (disponible ~7-16 jours max)
      const timelineMatch = this.timeline.find((t) => {
        const td = new Date(t.date);
        return (
          td.getFullYear() === date.getFullYear() &&
          td.getMonth() === date.getMonth() &&
          td.getDate() === date.getDate()
        );
      });
      const weather = timelineMatch?.weather?.icon
        ? {
            icon: timelineMatch.weather.icon,
            temperature: timelineMatch.weather.temperature,
            temperatureMin: (timelineMatch.weather as any).temperatureMin,
            temperatureMax: (timelineMatch.weather as any).temperatureMax,
            condition: (timelineMatch.weather as any).condition,
          }
        : undefined;

      days.push({
        date,
        isToday: date.toDateString() === new Date().toDateString(),
        isWeekend: [0, 6].includes(date.getDay()),
        weather,
        servicesCount: eventNames.length,
        eventNames,
        tasks: 0,
        completedTasks: 0,
        alertsCount: 0,
        specialPeriods: activePeriods.map((p) => ({
          label: p.label,
          type: p.type,
          color: p.color,
        })),
      });
    }

    this.calendarDays = days;
  }

  setCalendarView(view: CalendarView): void {
    if (
      (view === '3m' || view === '1y') &&
      !this.planFeatureService.canAccess(this.FEATURES.CALENDAR_EXTENDED)
    ) {
      return;
    }
    this.calendarView.set(view);
    this.calendarOffset.set(0);
    this.generateCalendar();
    this.cdr.markForCheck();
  }

  navigateCalendar(dir: -1 | 1): void {
    this.calendarOffset.update((o) => o + dir);
    this.generateCalendar();
    this.cdr.markForCheck();
  }

  resetCalendarToToday(): void {
    this.calendarOffset.set(0);
    this.generateCalendar();
    this.cdr.markForCheck();
  }

  getCalendarRangeLabel(): string {
    const view = this.calendarView();
    const offset = this.calendarOffset();
    const now = new Date();
    const locale = 'fr-FR';
    if (view === '1w') {
      const start = new Date(now);
      start.setDate(now.getDate() + offset * 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (view === '1m') {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    } else if (view === '3m') {
      const start = new Date(now);
      start.setMonth(now.getMonth() + offset * 3);
      const end = new Date(start);
      end.setMonth(start.getMonth() + 3);
      return `${start.toLocaleDateString(locale, { month: 'short', year: 'numeric' })} – ${end.toLocaleDateString(locale, { month: 'short', year: 'numeric' })}`;
    } else {
      const d = new Date(now.getFullYear() + offset, 0, 1);
      return d.getFullYear().toString();
    }
  }

  selectCalendarDay(day: CalendarDay): void {
    this.selectedCalendarDay = day;
    // Try to find a matching TimelineDay to open the rich modal
    const target = new Date(day.date).toDateString();
    const match = this.timeline.find(
      (t) => new Date(t.date).toDateString() === target,
    );
    if (match) {
      // Enrich with specialPeriods from CalendarDay (timeline may not carry it)
      this.selectTimelineDay({
        ...match,
        specialPeriods: day.specialPeriods ?? [],
      });
    } else {
      // Build a minimal TimelineDay from CalendarDay data
      // Populate events from eventsByStatus (covers days beyond the 7-day timeline)
      const allEvents = [
        ...this.eventsByStatus.planned,
        ...this.eventsByStatus.active,
      ];
      const dayEvents = allEvents.filter((e) => {
        const ed = new Date((e as any).eventDate ?? '');
        return (
          !isNaN(ed.getTime()) &&
          ed.getFullYear() === day.date.getFullYear() &&
          ed.getMonth() === day.date.getMonth() &&
          ed.getDate() === day.date.getDate()
        );
      });

      const synth: TimelineDay = {
        date: new Date(day.date),
        dayName: new Date(day.date).toLocaleDateString('fr-FR', {
          weekday: 'long',
        }),
        dayNumber: new Date(day.date).getDate(),
        isToday: day.isToday,
        weather: day.weather ?? undefined,
        events: dayEvents,
        opportunities: [],
        specialPeriods: day.specialPeriods ?? [],
      };
      this.selectTimelineDay(synth);
    }
    this.cdr.markForCheck();
  }

  getLeadingBlanks(): number[] {
    if (!this.calendarDays.length) return [];
    const first = this.calendarDays[0].date;
    const dow = (first.getDay() + 6) % 7;
    return Array(dow).fill(0);
  }

  openPeriodForm(period: MerchantPeriod | null = null): void {
    this.editingPeriod.set(period);
    this.showPeriodForm.set(true);
  }

  closePeriodForm(): void {
    this.showPeriodForm.set(false);
    this.editingPeriod.set(null);
  }

  onPeriodSaved(): void {
    this.closePeriodForm();
    this.merchantPeriods.set(this.merchantPeriodService.periods());
    this.generateCalendar();
    // Refresh selectedCalendarDay so modal shows updated period info
    if (this.selectedCalendarDay) {
      const refreshed = this.calendarDays.find((d) =>
        this.isSameDay(d.date, this.selectedCalendarDay!.date),
      );
      if (refreshed) this.selectedCalendarDay = refreshed;
    }
    this.cdr.markForCheck();
  }

  deletePeriod(period: MerchantPeriod): void {
    this.merchantPeriodService.delete(period._id).subscribe({
      next: () => {
        this.merchantPeriods.set(this.merchantPeriodService.periods());
        this.generateCalendar();
        this.cdr.markForCheck();
      },
    });
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  // ─── Data loading ───────────────────────────────

  private loadBusinessName() {
    this.authService
      .getMe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.user) {
            this.businessName = response.user.businessName || 'Commerce';
            this.activityType = (response.user.activityType as string) || '';
            this.affluence = (response.user.affluence as Affluence) || '';
            const services = response.user.services || [];
            this.userServices = (
              Array.isArray(services) ? services : []
            ) as ServiceTime[];

            // Phase 6: Position du marchand pour la carte
            const loc = (response.user as any).location;
            if (loc) {
              // Localisation affichée (ville — pays)
              if (loc.city) {
                this.userAddress = loc.country
                  ? loc.city + ' — ' + loc.country
                  : loc.city;
              }
              // Coordonnées pour la carte (peuvent être dans loc.coordinates ou loc directement)
              const coords = loc.coordinates || loc;
              if (coords && coords.lat && coords.lng) {
                // Rayon : soit personnalisé par l'utilisateur, soit défaut selon activité
                const defaultRadius =
                  this.DEFAULT_RADIUS_BY_ACTIVITY[this.activityType] ||
                  this.DEFAULT_RADIUS;
                const actionRadius = loc.actionRadius || defaultRadius;

                this.merchantLocation = {
                  lat: coords.lat,
                  lng: coords.lng,
                  address: loc.address || '',
                  city: loc.city || response.user.locationCity || '',
                  actionRadius,
                };
                this.log.debug(
                  '📍 MerchantLocation initialisé:',
                  this.merchantLocation,
                  `(défaut activité ${this.activityType}: ${defaultRadius}km)`,
                );

                // Alertes unifiées chargées dans applyDashboardData
                // (nécessite eventsByStatus pour exclure les événements déjà planifiés)
              } else {
                this.log.warn(
                  '⚠️ Coordonnées non trouvées dans location:',
                  loc,
                );
              }
            }
          }
          this.cdr.markForCheck();
        },
        error: (err) => this.log.error('Erreur profil:', err),
      });
  }

  private loadDashboard() {
    this.loading = true;
    this.dashboardService
      .loadDashboard()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.applyDashboardData(data);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('Erreur chargement dashboard:', err);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private applyDashboardData(data: any): void {
    this.kpis = data.kpis;
    this.eventsByStatus = data.eventsByStatus;
    this.timeline = data.timeline;
    this.weatherAlerts = data.weatherAlerts;
    this.insights = data.insights;
    this.topEvents = data.topEvents;
    this.opportunities = data.opportunities;
    this.precisionHistory = data.precisionHistory;

    this.enrichTimelineEventWeather();
    this.refreshActiveEventsWeather();
    this.applyOpportunityFilters();

    this.mapEvents = [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
      ...this.eventsByStatus.completed,
      ...this.eventsByStatus.analyzed,
    ]
      .filter((e: any) => e.lat && e.lng)
      .map((e: any) => ({
        id: e.id,
        name: e.name,
        lat: e.lat,
        lng: e.lng,
        location: e.location,
        eventType: e.eventType,
        status: e.status,
        date: e.eventDate,
      }));

    this.isEmptyState =
      this.eventsByStatus.planned.length === 0 &&
      this.eventsByStatus.active.length === 0 &&
      this.eventsByStatus.completed.length === 0 &&
      this.eventsByStatus.analyzed.length === 0;

    this.selectedTimelineDay =
      this.timeline.find(
        (d) => d.events.length > 0 || d.opportunities.length > 0,
      ) ||
      this.timeline[0] ||
      null;

    this.refreshNextEventWeatherIfChanged();

    this.refreshService.scheduleTransitions([
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
    ] as AppEvent[]);
    this.loading = false;

    // Regenerer le calendrier avec les données d'événements mises à jour
    this.generateCalendar();

    // Alertes unifiées — exclure les événements déjà planifiés par le marchand
    this.loadUnifiedAlerts();
  }

  /**
   * Charge les alertes unifiées (météo + événements + calendrier)
   * en excluant les événements que le marchand a déjà planifiés ou qui sont actifs.
   */
  private loadUnifiedAlerts(): void {
    if (!this.merchantLocation?.lat || !this.merchantLocation?.lng) return;
    const city = this.merchantLocation.city || '';
    const merchantEventNames = [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
    ].map((e: any) => e.name as string);

    this.alertService
      .loadAlerts(this.merchantLocation.lat, this.merchantLocation.lng, city, {
        excludeEventNames: merchantEventNames,
      })
      .subscribe((alerts) => {
        this.unifiedAlerts = alerts;
        this.cdr.markForCheck();
      });
  }

  /**
   * Reclassifie les événements planned/active en mémoire selon l'heure courante.
   * Zéro appel HTTP — utilisé lors des franchissements startTime/endTime.
   */
  private reclassifyEventsLocally(): void {
    const prevNextId = this.nextEvent?.id;
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const allDynamic = [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
    ];
    const newPlanned: any[] = [];
    const newActive: any[] = [];
    const newCompleted: any[] = [...this.eventsByStatus.completed];

    for (const evt of allDynamic) {
      const eventDay = new Date(evt.eventDate);
      eventDay.setHours(0, 0, 0, 0);

      const startDt = new Date(evt.eventDate);
      if (evt.startTime) {
        const [h, m] = evt.startTime.split(':').map(Number);
        startDt.setHours(h, m, 0, 0);
      } else {
        startDt.setHours(0, 0, 0, 0);
      }

      const endDt = new Date(evt.eventDate);
      if (evt.endTime) {
        const [h, m] = evt.endTime.split(':').map(Number);
        endDt.setHours(h, m, 0, 0);
      } else {
        endDt.setHours(23, 59, 0, 0);
      }

      if (endDt <= now) {
        newCompleted.push(evt);
      } else if (eventDay.getTime() === today.getTime() && startDt <= now) {
        newActive.push(evt);
      } else {
        newPlanned.push(evt);
      }
    }

    this.eventsByStatus = {
      ...this.eventsByStatus,
      planned: newPlanned,
      active: newActive,
      completed: newCompleted,
    };

    // Mettre à jour les statuts des events dans la timeline
    this.timeline = this.timeline.map((day) => ({
      ...day,
      events: day.events.map((evt: any) => {
        const startDt = new Date(evt.eventDate);
        if (evt.startTime) {
          const [h, m] = (evt.startTime as string).split(':').map(Number);
          startDt.setHours(h, m, 0, 0);
        } else {
          startDt.setHours(0, 0, 0, 0);
        }
        const endDt = new Date(evt.eventDate);
        if (evt.endTime) {
          const [h, m] = (evt.endTime as string).split(':').map(Number);
          endDt.setHours(h, m, 59, 999);
        } else {
          endDt.setHours(23, 59, 59, 999);
        }
        let status = evt.status;
        if (status !== 'completed') {
          if (endDt <= now) {
            status = 'completed';
          } else if (startDt <= now) {
            status = 'active';
          } else {
            status = evt.status === 'active' ? 'planned' : evt.status;
          }
        }
        return { ...evt, status };
      }),
    }));

    // Replanifier le prochain timer avec les données recalculées
    this.refreshService.scheduleTransitions([
      ...newPlanned,
      ...newActive,
    ] as AppEvent[]);

    this.refreshNextEventWeatherIfChanged();
  }

  // ─── Computed helpers ───────────────────────────

  get nextEvent(): AppEvent | null {
    return this.eventsByStatus.planned[0] || null;
  }

  get hasCompletedWithoutSales(): boolean {
    return this.eventsByStatus.completed.length > 0;
  }

  get completedCount(): number {
    return this.eventsByStatus.completed.length;
  }

  get totalPlanned(): number {
    return this.eventsByStatus.planned.length;
  }

  // ─── Timeline ───────────────────────────────────

  showTimelineModal = false;

  selectTimelineDay(day: TimelineDay) {
    this.selectedTimelineDay = day;
    this.showTimelineModal = true;
  }

  closeTimelineModal() {
    this.showTimelineModal = false;
  }

  findEventById(id: string): any {
    const all = [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
      ...this.eventsByStatus.completed,
      ...this.eventsByStatus.analyzed,
    ];
    return all.find((e: any) => String(e.id) === String(id));
  }

  // ─── Event helpers ──────────────────────────────

  getEventIcon(eventType: string): string {
    const found = EVENT_TYPES.find((t) => t.value === eventType);
    return found ? found.icon : '📍';
  }

  getEventTypeLabel(eventType: string): string {
    const found = EVENT_TYPES.find((t) => t.value === eventType);
    return found ? found.label : eventType;
  }

  getDaysUntil(dateStr: string | Date): number {
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = target.getTime() - now.getTime();
    return Math.round(diff / (1000 * 60 * 60 * 24));
  }

  getDaysUntilLabel(dateStr: string | Date): string {
    const days = this.getDaysUntil(dateStr);
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return 'Demain';

    const abs = Math.abs(days);
    const past = days < 0;
    const prefix = past ? 'Il y a' : 'Dans';

    if (abs < 30) {
      return `${prefix} ${abs}j`;
    }

    const years = Math.floor(abs / 365);
    const months = Math.floor((abs % 365) / 30);
    const remainDays = abs % 30;

    const parts: string[] = [];
    if (years > 0) parts.push(`${years}an${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months}mois`);
    if (remainDays > 0) parts.push(`${remainDays}j`);

    return `${prefix} ${parts.join(' ')}`;
  }

  formatEventDate(dateStr: string | Date): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  getWeatherIcon(condition: string | undefined): string {
    if (!condition) return '🌤️';
    return (WEATHER_ICONS as any)[condition] || '🌤️';
  }

  // ─── Météo prochain événement ────────────────────

  private _lastLoadedWeatherEventId: string | null = null;

  /**
   * Recharge la météo du prochain événement seulement si celui-ci a changé.
   * À appeler après toute mutation de eventsByStatus.planned.
   */
  private refreshNextEventWeatherIfChanged(): void {
    const currentId = this.nextEvent?.id ?? null;
    if (currentId === this._lastLoadedWeatherEventId) return; // rien à faire
    this.nextEventWeather = null;
    this._lastLoadedWeatherEventId = currentId;
    if (currentId) this.loadNextEventDetails(currentId);
  }

  /** Charge les créneaux horaires des événements actifs en cours. */
  private refreshActiveEventsWeather(): void {
    this.activeEventHourlyWeather.clear();
    for (const event of this.eventsByStatus.active as any[]) {
      const loc = event.location as any;
      const lat: number | undefined = event.lat ?? loc?.coordinates?.lat;
      const lng: number | undefined = event.lng ?? loc?.coordinates?.lng;
      if (!lat || !lng) continue;
      const dateStr = new Date(event.eventDate).toISOString().slice(0, 10);
      this.locationService
        .getWeatherSlots(lat, lng, dateStr, event.startTime, event.endTime)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((w) => {
          this.activeEventHourlyWeather.set(String(event.id), w);
          this.cdr.markForCheck();
        });
    }
  }

  hasActiveEventCoords(event: any): boolean {
    const loc = event.location as any;
    return !!(event.lat ?? loc?.coordinates?.lat);
  }

  getActiveEventWeatherIcon(eventId: string): string {
    const w = this.timelineEventWeather.get(String(eventId));
    if (!w?.available) return '🌡️';
    return this.wmoToEmoji((w as any).weatherCode);
  }

  getActiveEventWeatherIsAlert(eventId: string): boolean {
    const w = this.timelineEventWeather.get(String(eventId));
    if (!w?.available) return false;
    return (w as any).weatherCode >= 61;
  }

  /** Wrapper public pour accès au template (slots horaires) */
  hourlySlotEmoji(code: number): string {
    return this.wmoToEmoji(code);
  }

  get isNextEventImminent(): boolean {
    if (!this.nextEvent) return false;
    return this.getDaysUntil(this.nextEvent.eventDate) <= 1;
  }

  get hasNextEventCoords(): boolean {
    const ev = this.nextEvent;
    if (!ev) return false;
    const loc = ev.location as any;
    return !!(ev.lat ?? loc?.coordinates?.lat);
  }

  getNextEventWeatherIcon(): string {
    const w = this.nextEventWeather;
    if (!w || !w.available) return '🌡️';
    const code = (w as any).weatherCode as number;
    if (code === 0 || code === 1) return '☀️';
    if (code === 2) return '🌤️';
    if (code === 3) return '☁️';
    if (code === 45 || code === 48) return '🌫️';
    if (code >= 51 && code <= 55) return '🌦️';
    if (code >= 61 && code <= 65) return '🌧️';
    if (code >= 71 && code <= 77) return '❄️';
    if (code >= 80 && code <= 82) return '🌦️';
    if (code >= 85 && code <= 86) return '🌨️';
    if (code >= 95) return '⚡';
    return '🌡️';
  }

  getNextEventWeatherLabel(): string {
    const w = this.nextEventWeather;
    if (!w) return '';
    if (!w.available) return 'Météo non disponible';
    const ww = w as Extract<EventWeatherForecast, { available: true }>;
    const min = Math.round(ww.temperatureMin);
    const max = Math.round(ww.temperatureMax);
    return `${ww.condition} · ${min}–${max}°C`;
  }

  getNextEventWeatherBadgeClass(): string {
    const w = this.nextEventWeather;
    if (!w || !w.available) return 'bg-white/10 text-purple-100';
    const code = (w as any).weatherCode as number;
    if (code >= 95) return 'bg-red-500/30 text-white';
    if (code >= 61 && code <= 82) return 'bg-blue-400/30 text-white';
    if (code === 0 || code === 1) return 'bg-yellow-300/20 text-yellow-100';
    return 'bg-white/10 text-purple-100';
  }

  getNextEventWeatherIsAlert(): boolean {
    const w = this.nextEventWeather;
    if (!w || !w.available) return false;
    const code = (w as any).weatherCode as number;
    return code >= 61;
  }

  // ─── Météo timeline (par événement) ───────────────

  private wmoToEmoji(code: number): string {
    if (code === 0 || code === 1) return '\u2600\ufe0f';
    if (code === 2) return '\ud83c\udf24\ufe0f';
    if (code === 3) return '\u2601\ufe0f';
    if (code === 45 || code === 48) return '\ud83c\udf2b\ufe0f';
    if (code >= 51 && code <= 55) return '\ud83c\udf26\ufe0f';
    if (code >= 61 && code <= 65) return '\ud83c\udf27\ufe0f';
    if (code >= 71 && code <= 77) return '\u2744\ufe0f';
    if (code >= 80 && code <= 82) return '\ud83c\udf26\ufe0f';
    if (code >= 85 && code <= 86) return '\ud83c\udf28\ufe0f';
    if (code >= 95) return '\u26a1';
    return '\ud83c\udf21\ufe0f';
  }

  /** Charge la météo pour chaque événement de la timeline ayant des coordonnées */
  private enrichTimelineEventWeather(): void {
    this.timelineEventWeather.clear();
    for (const day of this.timeline) {
      for (const ev of day.events as any[]) {
        const loc = ev.location as any;
        const lat: number | undefined = ev.lat ?? loc?.coordinates?.lat;
        const lng: number | undefined = ev.lng ?? loc?.coordinates?.lng;
        if (!lat || !lng || !ev.eventDate) continue;
        const dateStr = new Date(ev.eventDate).toISOString().slice(0, 10);
        this.locationService
          .getWeatherForDate(lat, lng, dateStr)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((w) => {
            this.timelineEventWeather.set(String(ev.id), w);
            this.cdr.markForCheck();
          });
      }
    }
  }

  /**
   * Icône météo à afficher dans l'en-tête du jour de la timeline.
   * - Pas d'événements : icône de base (lieu du marchand)
   * - Événements avec même météo : icône unique
   * - Événements avec météos différentes : null (chaque badge affiche le sien)
   */
  getTimelineDayWeatherIcon(day: TimelineDay): string | null {
    const events = (day as any).events as any[];
    if (!events || events.length === 0) return day.weather?.icon || null;

    const codes: number[] = [];
    for (const ev of events) {
      const w = this.timelineEventWeather.get(String(ev.id));
      if (w?.available) codes.push((w as any).weatherCode);
    }

    if (codes.length === 0) return day.weather?.icon || null; // pas encore chargé → base
    if (codes.every((c) => c === codes[0])) return this.wmoToEmoji(codes[0]);
    return null; // différentes → per-event
  }

  getTimelineDayWeatherTemp(day: TimelineDay): string {
    const events = (day as any).events as any[];
    if (events && events.length > 0) {
      for (const ev of events) {
        const w = this.timelineEventWeather.get(String(ev.id));
        if (w?.available) {
          const ww = w as Extract<EventWeatherForecast, { available: true }>;
          return `${Math.round(ww.temperatureMin)}\u2013${Math.round(ww.temperatureMax)}\u00b0C`;
        }
      }
    }
    return day.weather?.temperature ? `${day.weather.temperature}\u00b0C` : '';
  }

  getTimelineIsPerEvent(day: TimelineDay): boolean {
    const events = (day as any).events as any[];
    if (!events || events.length < 2) return false;
    const codes: number[] = [];
    for (const ev of events) {
      const w = this.timelineEventWeather.get(String(ev.id));
      if (w?.available) codes.push((w as any).weatherCode);
    }
    return codes.length >= 2 && !codes.every((c) => c === codes[0]);
  }

  getTimelineEventEmoji(eventId: string | undefined): string {
    if (!eventId) return '';
    const w = this.timelineEventWeather.get(String(eventId));
    if (!w?.available) return '';
    return this.wmoToEmoji((w as any).weatherCode);
  }

  getAlertSeverityClasses(severity: string): string {
    switch (severity) {
      case 'danger':
        return 'border-red-500 bg-red-50';
      case 'warning':
        return 'border-yellow-500 bg-yellow-50';
      default:
        return 'border-green-500 bg-green-50';
    }
  }

  getInsightClasses(type: string): string {
    switch (type) {
      case 'opportunity':
        return 'border-green-500 bg-green-50 dark:bg-green-900/20';
      case 'pattern':
        return 'border-purple-500 bg-purple-50 dark:bg-purple-900/20';
      case 'warning':
        return 'border-orange-500 bg-orange-50 dark:bg-orange-900/20';
      default:
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
    }
  }

  // ─── Phase 2: Moteur reco ───────────────────────

  private loadNextEventDetails(eventId: string) {
    this._lastLoadedWeatherEventId = eventId;
    this.dashboardService
      .getRecommendations(eventId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (recos) => {
          this.nextEventRecos = recos;
          this.cdr.markForCheck();
        },
        error: () => {
          this.nextEventRecos = null;
          this.cdr.markForCheck();
        },
      });

    this.dashboardService
      .getChecklist(eventId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (checklist) => {
          this.nextEventChecklist = checklist;
          this.cdr.markForCheck();
        },
        error: () => {
          this.nextEventChecklist = null;
          this.cdr.markForCheck();
        },
      });

    // Météo : lat/lng direct sur l'event ou via EventLocation.coordinates
    const ev = this.nextEvent;
    if (!ev) return;
    const loc = ev.location as any;
    const lat: number | undefined = ev.lat ?? loc?.coordinates?.lat;
    const lng: number | undefined = ev.lng ?? loc?.coordinates?.lng;
    if (lat && lng) {
      const dateStr = new Date(ev.eventDate).toISOString().slice(0, 10);
      this.locationService
        .getWeatherForDate(lat, lng, dateStr)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((w) => {
          this.nextEventWeather = w;
          this.cdr.markForCheck();
        });
    }
  }

  toggleRecoDetail() {
    this.showRecoDetail = !this.showRecoDetail;
  }

  getFormulaResult(p: RecoProduct): number {
    const c = p.coefficients;
    return Math.round(c.base * c.eventType * c.weather * c.duration);
  }

  getRangePercentage(p: RecoProduct, value: number): number {
    const range = p.rangeHigh - p.rangeLow;
    if (range <= 0) return 50;
    return Math.min(100, Math.max(0, ((value - p.rangeLow) / range) * 100));
  }

  get precisionGain(): number {
    if (this.precisionHistory.length < 2) return 0;
    const first = this.precisionHistory[0].precision;
    const last =
      this.precisionHistory[this.precisionHistory.length - 1].precision;
    return last - first;
  }

  get precisionEventCount(): number {
    return this.precisionHistory.length;
  }

  getPrecisionBarWidth(point: PrecisionPoint): string {
    return `${point.precision}%`;
  }

  getPrecisionColor(precision: number): string {
    if (precision >= 85) return 'bg-green-500';
    if (precision >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  }

  // ─── Phase 3: Checklist ─────────────────────────

  get checklistItems(): ChecklistItem[] {
    if (!this.nextEventChecklist) return [];
    if (this.checklistFilter === 'all') return this.nextEventChecklist.items;
    return this.nextEventChecklist.items.filter(
      (i) => i.category === this.checklistFilter,
    );
  }

  get checklistProgress(): number {
    if (!this.nextEventChecklist || this.nextEventChecklist.items.length === 0)
      return 0;
    const completed = this.nextEventChecklist.items.filter(
      (i) => i.completed,
    ).length;
    return Math.round((completed / this.nextEventChecklist.items.length) * 100);
  }

  get checklistCompletedCount(): number {
    if (!this.nextEventChecklist) return 0;
    return this.nextEventChecklist.items.filter((i) => i.completed).length;
  }

  get checklistCategories(): { key: string; label: string; icon: string }[] {
    return [
      { key: 'all', label: 'Tout', icon: '📋' },
      { key: 'approvisionnement', label: 'Appro', icon: '🛒' },
      { key: 'logistique', label: 'Logistique', icon: '🚚' },
      { key: 'meteo', label: 'Météo', icon: '🌦️' },
      { key: 'administratif', label: 'Admin', icon: '📄' },
      { key: 'conservation', label: 'Conservation', icon: '❄️' },
    ];
  }

  toggleChecklistItem(item: ChecklistItem) {
    item.completed = !item.completed;
  }

  setChecklistFilter(filter: string) {
    this.checklistFilter = filter;
  }

  getCategoryIcon(category: string): string {
    const map: Record<string, string> = {
      approvisionnement: '🛒',
      logistique: '🚚',
      meteo: '🌦️',
      administratif: '📄',
      conservation: '❄️',
    };
    return map[category] || '📋';
  }

  // ─── Navigation ─────────────────────────────────

  onRadiusChanged(radius: number): void {
    if (this.merchantLocation) {
      this.merchantLocation = {
        ...this.merchantLocation,
        actionRadius: radius,
      };
    }
    // Re-filter opportunities with the new radius
    this.applyOpportunityFilters();
    // Debounced save to backend
    this.radiusChange$.next(radius);
  }

  private saveRadius(radius: number): void {
    const user = this.authService.getCurrentUser();
    const userId = (user as any)?.id || (user as any)?._id;
    if (!userId) return;

    this.locationService
      .updateLocationProfile(userId, { actionRadius: radius })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.log.debug('✅ Rayon sauvegardé:', radius, 'km'),
        error: (err) => this.log.error('❌ Erreur sauvegarde rayon:', err),
      });
  }

  private reloadOpportunities(): void {
    this.dashboardService
      .getOpportunities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (opps) => {
          this.opportunities = opps;
          this.applyOpportunityFilters();
          this.cdr.markForCheck();
        },
        error: (err) =>
          this.log.error('❌ Erreur rechargement opportunités:', err),
      });
  }

  /** Suivre une opportunité (la convertir en événement) */
  onOpportunityFollowed(opportunity: any): void {
    const allEvents = [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
    ];
    const candidate = {
      id: opportunity.id ?? '',
      name: opportunity.name,
      eventDate: opportunity.date,
      startTime: opportunity.startTime ?? null,
      endTime: opportunity.endTime ?? null,
    };
    const conflicts = this.eventConflictService.findConflicts(
      allEvents,
      candidate,
    );
    if (conflicts.length > 0) {
      this.pendingOpportunity = opportunity;
      this.conflictWarning = conflicts;
      return;
    }
    this.doFollowOpportunity(opportunity);
  }

  onConflictConfirm(): void {
    this.conflictWarning = [];
    if (this.pendingOpportunity) {
      this.doFollowOpportunity(this.pendingOpportunity);
      this.pendingOpportunity = null;
    }
  }

  onConflictCancel(): void {
    this.conflictWarning = [];
    this.pendingOpportunity = null;
  }

  private doFollowOpportunity(opportunity: any): void {
    // Appel immédiat pour feedback instantané — routeData ajouté en arrière-plan après
    this.dashboardService
      .followOpportunity(opportunity)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.log.debug('✅ Opportunité suivie:', opportunity.name);
            // Marquer localement l'opportunité comme suivie
            if (!this.followedOpportunityIds.includes(opportunity.id)) {
              this.followedOpportunityIds = [
                ...this.followedOpportunityIds,
                opportunity.id,
              ];
            }

            // Récupérer l'event créé côté backend si disponible
            const newEvent = response.event || null;
            if (newEvent) {
              const eventId = newEvent._id || newEvent.id || null;
              if (eventId) {
                // Calculer et persister routeData en arrière-plan (sans bloquer l'UI)
                this.fetchAndPersistRouteData(opportunity, eventId);

                // stocker mapping externalId -> eventId
                this.followedEventMap = {
                  ...this.followedEventMap,
                  [opportunity.id]: eventId,
                };

                // Normaliser les champs pour l'affichage (location string, margins, attendance)
                const normalizedLocation =
                  typeof newEvent.location === 'string'
                    ? newEvent.location
                    : newEvent.location?.city ||
                      newEvent.location?.address ||
                      newEvent.location ||
                      opportunity.location ||
                      '';

                // Try event margins, fallback to opportunity margins/revenue
                let normalizedMargin:
                  | { low: number; high: number }
                  | undefined = undefined;
                if (newEvent.estimatedMargin) {
                  if (typeof newEvent.estimatedMargin === 'number') {
                    normalizedMargin = {
                      low: newEvent.estimatedMargin,
                      high: newEvent.estimatedMargin,
                    };
                  } else {
                    normalizedMargin = {
                      low: newEvent.estimatedMargin.low ?? 0,
                      high: newEvent.estimatedMargin.high ?? 0,
                    };
                  }
                } else if (opportunity.estimatedMargin) {
                  normalizedMargin = {
                    low:
                      opportunity.estimatedMargin.low ??
                      opportunity.estimatedMargin ??
                      0,
                    high:
                      opportunity.estimatedMargin.high ??
                      opportunity.estimatedMargin ??
                      0,
                  };
                } else if (opportunity.estimatedRevenue) {
                  // if only revenue is available, keep margin undefined (we don't compute margin from revenue here)
                  normalizedMargin = undefined;
                }

                const normalizedAttendance =
                  newEvent.expectedAttendance ||
                  opportunity.expectedAttendance ||
                  opportunity.expectedCustomers ||
                  null;

                // Ajouter l'événement dans la liste 'planned' pour affichage immédiat
                const newAppEvent: any = {
                  id: eventId,
                  externalId: opportunity.id,
                  name: newEvent.name || opportunity.name,
                  location: normalizedLocation,
                  eventDate:
                    newEvent.eventDate ||
                    newEvent.date ||
                    opportunity.date ||
                    null,
                  startTime:
                    newEvent.startTime || opportunity.startTime || null,
                  endTime: newEvent.endTime || opportunity.endTime || null,
                  expectedAttendance: normalizedAttendance,
                  estimatedMargin: normalizedMargin,
                  estimatedRevenue:
                    newEvent.estimatedRevenue || opportunity.estimatedRevenue,
                  expectedCustomers:
                    newEvent.expectedCustomers || opportunity.expectedCustomers,
                  score: newEvent.score || opportunity.score,
                  capacityUsed:
                    newEvent.capacityUsed || opportunity.capacityUsed,
                  status: newEvent.status || 'planned',
                  eventType:
                    newEvent.eventType ||
                    newEvent.type ||
                    opportunity.eventType ||
                    opportunity.type ||
                    null,
                  lat: newEvent.location?.coordinates?.lat || opportunity.lat,
                  lng: newEvent.location?.coordinates?.lng || opportunity.lng,
                } as any;

                this.eventsByStatus = {
                  ...this.eventsByStatus,
                  planned: [newAppEvent, ...this.eventsByStatus.planned],
                };

                // Injecter dans la timeline si dans la fenêtre 7j
                if (newAppEvent.eventDate) {
                  const evtDate = new Date(newAppEvent.eventDate);
                  const toLocalStr = (d: Date) =>
                    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const evtDateStr = toLocalStr(evtDate);
                  this.timeline = this.timeline.map((day) => {
                    const dayStr = toLocalStr(new Date(day.date));
                    if (dayStr === evtDateStr) {
                      return { ...day, events: [...day.events, newAppEvent] };
                    }
                    return day;
                  });
                }

                // Mettre à jour la carte
                this.rebuildMapEvents();

                // Marquer visuellement l'event ajouté
                this.highlightedEventId = eventId;
                setTimeout(() => {
                  this.highlightedEventId = null;
                  this.cdr.markForCheck();
                }, 2500);
              }
            }

            // Recalculer les opportunités recommandées (pour exclure celle suivie)
            this.applyOpportunityFilters();
            // Reschedule transition timers with the newly added event
            this.refreshService.scheduleTransitions([
              ...this.eventsByStatus.planned,
              ...this.eventsByStatus.active,
            ] as AppEvent[]);
            // Si l'événement ajouté est déjà en cours, reclassifier immédiatement
            this.reclassifyEventsLocally();
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          // Ignorer silencieusement les erreurs 400 (déjà suivi)
          if (err.status === 400) {
            this.log.debug('ℹ️ Opportunité déjà suivie:', opportunity.name);
          } else if (
            err.status === 403 &&
            err.error?.code === 'EVENT_LIMIT_REACHED'
          ) {
            if (confirm(`${err.error.message}\n\nVoir les plans ?`)) {
              this.router.navigate(['/subscription']);
            }
          } else {
            this.log.error('❌ Erreur suivi opportunité:', err);
          }
        },
      });
  }

  createEvent() {
    this.router.navigate(['/events/create']);
  }

  viewEvent(eventId: string) {
    this.router.navigate(['/events', eventId]);
  }

  goToKiosk(eventId: string) {
    this.router.navigate(['/kiosk'], { queryParams: { eventId } });
  }

  enterSalesEntry(eventId: string) {
    this.router.navigate(['/sales/entry-itinerant'], {
      queryParams: { eventId },
    });
  }

  /** Récupère l'itinéraire OSRM en arrière-plan et le persiste sur l'événement créé */
  private fetchAndPersistRouteData(opportunity: any, eventId: string): void {
    const ml = this.merchantLocation;
    if (!ml?.lat || !ml?.lng || !opportunity.lat || !opportunity.lng) return;

    this.locationService
      .getRoute(
        { lat: ml.lat, lng: ml.lng },
        { lat: opportunity.lat, lng: opportunity.lng },
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((routes) => {
        const r = routes?.[0];
        if (!r) return;
        this.eventService
          .patchRouteData(eventId, r)
          .pipe(catchError(() => of(null)))
          .subscribe();
      });
  }

  viewOpportunities() {
    this.router.navigate(['/opportunities']);
  }

  viewAnalytics() {
    this.router.navigate(['/analytics']);
  }

  enrollOpportunity(opp: Opportunity) {
    // Pré-remplir un événement depuis l'opportunité
    this.router.navigate(['/events/create'], {
      queryParams: {
        name: opp.name,
        location: opp.location,
        eventType: opp.eventType,
        date: opp.date,
      },
    });
  }

  // ─── Filtres opportunités ───────────────────────
  /** Retourne le label d'une semaine à partir d'un offset (0=courante, 1=suivante…) */
  getWeekLabel(offset: number): string {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) =>
      d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    const label =
      offset === 0
        ? 'Cette semaine'
        : offset === 1
          ? 'Semaine prochaine'
          : `Sem. +${offset}`;
    return `${label} (${fmt(monday)}–${fmt(sunday)})`;
  }

  /** 8 semaines glissantes pour le sélecteur (semaine 0 → semaine 7) */
  get weekOptions(): { value: string; label: string }[] {
    return Array.from({ length: 8 }, (_, i) => ({
      value: `week${i}`,
      label: this.getWeekLabel(i),
    }));
  }

  applyOpportunityFilters(): void {
    let filtered = [...this.opportunities];

    // Toujours exclure les opportunités dont la date est passée
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    filtered = filtered.filter((opp) => new Date(opp.date) >= today);

    // Toujours filtrer par rayon d'action
    const radiusKm = this.merchantLocation?.actionRadius ?? 100;
    filtered = filtered.filter((opp) => opp.distanceKm <= radiusKm);

    // Identifier les opportunités déjà suivies (pour affichage grisé sur la carte)
    const followedIds = new Set<string>();
    [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
      ...this.eventsByStatus.completed,
      ...this.eventsByStatus.analyzed,
    ].forEach((event) => {
      if (event.externalId) {
        followedIds.add(event.externalId);
      }
    });
    this.followedOpportunityIds = Array.from(followedIds);
    // Construire le mapping externalId -> eventId pour navigation immédiate
    const eventMap: Record<string, string> = {};
    [
      ...this.eventsByStatus.planned,
      ...this.eventsByStatus.active,
      ...this.eventsByStatus.completed,
      ...this.eventsByStatus.analyzed,
    ].forEach((event) => {
      if (event.externalId) {
        eventMap[event.externalId] = event.id;
      }
    });
    this.followedEventMap = eventMap;

    // Filtre par période
    if (this.oppFilterPeriod !== 'all') {
      const now = new Date();
      // Lundi de la semaine courante
      const currentMonday = new Date(now);
      currentMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      currentMonday.setHours(0, 0, 0, 0);

      filtered = filtered.filter((opp) => {
        const oppDate = new Date(opp.date);

        // Semaines : week+N → du lundi N*7 jours plus tard au dimanche
        const weekMatch = this.oppFilterPeriod.match(/^week(\d+)$/);
        if (weekMatch) {
          const offset = parseInt(weekMatch[1], 10);
          const start = new Date(currentMonday);
          start.setDate(start.getDate() + offset * 7);
          const end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          return oppDate >= start && oppDate <= end;
        }

        switch (this.oppFilterPeriod) {
          case 'month': {
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const endOfMonth = new Date(
              now.getFullYear(),
              now.getMonth() + 1,
              0,
              23,
              59,
              59,
            );
            return oppDate >= startOfMonth && oppDate <= endOfMonth;
          }
          case 'weekend': {
            const day = oppDate.getDay();
            return day === 0 || day === 6;
          }
          case '3months': {
            const in3months = new Date(now);
            in3months.setMonth(in3months.getMonth() + 3);
            return oppDate >= now && oppDate <= in3months;
          }
          case '6months': {
            const in6months = new Date(now);
            in6months.setMonth(in6months.getMonth() + 6);
            return oppDate >= now && oppDate <= in6months;
          }
          default:
            return true;
        }
      });
    }

    // Filtre par rentabilité
    if (this.oppFilterProfitability !== 'all') {
      filtered = filtered.filter(
        (opp) => opp.profitabilityLevel === this.oppFilterProfitability,
      );
    }

    // Filtre par type
    if (this.oppFilterType !== 'all') {
      filtered = filtered.filter((opp) => opp.eventType === this.oppFilterType);
    }

    // Exclure des recommandations les opportunités déjà suivies
    if (this.followedOpportunityIds && this.followedOpportunityIds.length > 0) {
      filtered = filtered.filter(
        (opp) => !this.followedOpportunityIds.includes(opp.id),
      );
    }

    // Tri
    const dir = this.oppSortDir === 'asc' ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      switch (this.oppSortBy) {
        case 'margin': {
          const mA =
            (a.estimatedMargin?.low ?? 0) + (a.estimatedMargin?.high ?? 0);
          const mB =
            (b.estimatedMargin?.low ?? 0) + (b.estimatedMargin?.high ?? 0);
          return (mA - mB) * dir;
        }
        case 'score':
          return ((a.score ?? 0) - (b.score ?? 0)) * dir;
        case 'date':
        default:
          return (
            (new Date(a.date).getTime() - new Date(b.date).getTime()) * dir
          );
      }
    });

    this.filteredOpportunities = filtered;
  }

  onSortByChange(): void {
    // Par défaut : marge et score → décroissant (plus intéressant en premier), date → croissant
    this.oppSortDir = this.oppSortBy === 'date' ? 'asc' : 'desc';
    this.applyOpportunityFilters();
  }

  resetOpportunityFilters(): void {
    this.oppFilterPeriod = 'all';
    this.oppFilterProfitability = 'all';
    this.oppFilterType = 'all';
    this.oppSortBy = 'date';
    this.oppSortDir = 'asc';
    // (oppShowAll conservé intentionnellement)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const radiusKm = this.merchantLocation?.actionRadius ?? 100;
    let base = this.opportunities.filter(
      (opp) => new Date(opp.date) >= today && opp.distanceKm <= radiusKm,
    );
    // Toujours exclure les opportunités déjà suivies
    if (this.followedOpportunityIds && this.followedOpportunityIds.length > 0) {
      this.filteredOpportunities = base.filter(
        (opp) => !this.followedOpportunityIds.includes(opp.id),
      );
    } else {
      this.filteredOpportunities = base;
    }
  }
}
